import { Suspense, lazy, useDeferredValue, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";

import { fetchAdbExplorer, type AdbCorrelationScore, type AdbExplorerColumn, type AdbExplorerLayer, type AdbExplorerPayload } from "../../api/adbExplorer";
import { fetchCompositeSources } from "../../api/composite";
import type { CorrelogramColumn } from "../../components/data-explorer/Correlogram";
import { rankColumnsByCorrelationTo } from "../../components/data-explorer/correlationRanking";
import type { ThreadRow } from "../thread-design-system";
import type { V3WorkbenchData } from "../thread-v3/types";
import { Icons } from "../thread-v4/icons";
import { formatValue, rowKey } from "../thread-v4/threadV4RowUtils";
import type { SamplePoint } from "../adaptive-sampling/types";
import { useAdaptiveCampaign, useCampaignPoints, type AdaptiveCampaignSelection } from "../adaptive-sampling/campaign-context";
import type { SurfaceProps } from "./ThreadV5Surfaces";
import {
  DEFAULT_TAILOR_TRUST_LIMITS,
  buildSuggestionAdopter,
  correctionForPair,
  deriveFeatureRidge,
  fitTailorSliceModel,
  predictTailorSliceModel,
  recommendTailorRuns,
  summarizeTailorEvidence,
  tailorValueForMode,
  type TailorEvidenceMode,
  type TailorTrustLimits,
} from "./adbTailorSlice";

const DataExplorerCorrelogram = lazy(() =>
  import("../../components/data-explorer/Correlogram").then((module) => ({ default: module.Correlogram })),
);
const LazyAdbCompositeSurface = lazy(() =>
  import("./ThreadV5AdbComposite").then((module) => ({ default: module.AdbCompositeSurface })),
);

const OUTPUT_PRIORITY = ["CL", "C_L", "cl", "CD", "C_D", "cd", "Cm", "C_m", "Cm_pitch", "CY", "Cn_yaw", "Cl_roll", "CL_alpha", "Cm_alpha"];
const OUTPUT_TOKENS = new Set(["cl", "cd", "cm", "cy", "cn", "cf", "lift", "drag", "moment", "force", "coefficient", "pressure", "friction", "omega", "stability", "metric", "output", "result"]);
const AXIS_TOKENS = new Set(["alpha", "aoa", "beta", "sideslip", "mach", "altitude", "re", "reynolds", "elevator", "aileron", "rudder", "flap", "rpm", "throttle", "thrust", "velocity", "speed", "theta", "phi", "gap", "deflection", "disk", "blow", "blowing", "loading"]);
const MODEL_LAYER_COLORS = ["#327a5f", "#1e6f8f", "#a7662b", "#7358a5", "#9c5d68", "#60733f"];
const TAILOR_LIVE_LAYER_ID = "tailor_live";
const TAILOR_LIVE_LAYER_COLOR = "#d98925";
const OUTPUT_VALUE_ALIASES: Record<string, string[]> = {
  CL: ["CL", "C_L", "cl"],
  C_L: ["C_L", "CL", "cl"],
  CD: ["CD", "C_D", "cd"],
  C_D: ["C_D", "CD", "cd"],
  CY: ["CY", "C_Y", "cy"],
  C_Y: ["C_Y", "CY", "cy"],
  Cm: ["Cm", "C_m", "Cmy", "cm"],
  C_m: ["C_m", "Cm", "Cmy", "cm"],
  Cmy: ["Cmy", "Cm", "C_m", "cm"],
};
const TAILOR_EVIDENCE_MODES: Array<{ id: TailorEvidenceMode; label: string; title: string }> = [
  { id: "raw", label: "Raw", title: "Sampled ND conditions projected into each visible 2D slice." },
  { id: "compatible", label: "Compatible", title: "Only sampled points close to the held-axis constants; direct slice evidence." },
  { id: "corrected", label: "Corrected", title: "Sampled values transported to each held slice with model(raw) -> model(slice) correction." },
];

function token(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function words(value: string): string[] {
  return value.toLowerCase().split(/[^a-z0-9]+/g).filter(Boolean);
}

function numericValue(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function labelFor(field: string): string {
  const known: Record<string, string> = {
    CL: "C_L",
    cl: "C_L",
    CD: "C_D",
    cd: "C_D",
    Cm: "C_m",
    cm: "C_m",
    Cm_pitch: "C_m pitch",
    Cl_roll: "C_l roll",
    Cn_yaw: "C_n yaw",
    CY: "C_Y",
    alpha: "alpha",
    beta: "beta",
    mach: "Mach",
    // Blowing axis (FXC-9009): actuator-disk thrust loading Δp/q∞. A friendly label
    // so the surface axis_x/axis_y selectors read "Blowing (Δp/q∞)", not the raw key.
    disk_thrust_loading: "Blowing (Δp/q∞)",
  };
  return known[field] ?? field.replace(/^_+/, "").replace(/_/g, " ");
}

function isMetadata(field: string): boolean {
  const normal = token(field);
  return field.startsWith("_") || normal.endsWith("id") || normal === "index" || normal === "statuscode" || normal.includes("timestamp");
}

function isOutput(field: string): boolean {
  const normal = token(field);
  if (OUTPUT_PRIORITY.map(token).includes(normal)) return true;
  if (/^(cl|cd|cm|cy|cn|cf)/.test(normal)) return true;
  return words(field).some((word) => OUTPUT_TOKENS.has(word));
}

function isAxis(field: string): boolean {
  return !isOutput(field) && words(field).some((word) => AXIS_TOKENS.has(word));
}

function columnStats(rows: Record<string, unknown>[], field: string): Pick<AdbExplorerColumn, "sample_count" | "min" | "max" | "mean" | "distinct_count"> {
  const values = rows.map((row) => numericValue(row[field])).filter((value): value is number => value !== null);
  if (!values.length) return { sample_count: 0, min: null, max: null, mean: null, distinct_count: 0 };
  return {
    sample_count: values.length,
    min: Math.min(...values),
    max: Math.max(...values),
    mean: values.reduce((sum, value) => sum + value, 0) / values.length,
    distinct_count: new Set(values.map((value) => value.toPrecision(12))).size,
  };
}

function pearson(rows: Record<string, unknown>[], xField: string, yField: string): number | null {
  const pairs = rows
    .map((row) => [numericValue(row[xField]), numericValue(row[yField])] as const)
    .filter((pair): pair is readonly [number, number] => pair[0] !== null && pair[1] !== null);
  if (pairs.length < 3) return null;
  const xs = pairs.map((pair) => pair[0]);
  const ys = pairs.map((pair) => pair[1]);
  const mx = xs.reduce((sum, value) => sum + value, 0) / xs.length;
  const my = ys.reduce((sum, value) => sum + value, 0) / ys.length;
  let num = 0;
  let dx = 0;
  let dy = 0;
  for (let i = 0; i < xs.length; i += 1) {
    const xv = xs[i]! - mx;
    const yv = ys[i]! - my;
    num += xv * yv;
    dx += xv * xv;
    dy += yv * yv;
  }
  return dx > 0 && dy > 0 ? num / Math.sqrt(dx * dy) : null;
}

function inferLayer(row: Record<string, unknown>): AdbExplorerLayer {
  const raw = ["model_layer", "fidelity", "source_tool", "source", "solver", "method", "family", "geometry_id"]
    .map((field) => row[field])
    .find((value) => value != null && String(value).trim());
  const text = String(raw ?? "observed");
  const normal = token(text);
  if (/corrected|transport|surrogate|residual/.test(normal)) return { id: "corrected", label: "Corrected / model-conditioned", evidence: "model-conditioned", count: 0 };
  if (/flow360|cfd|rans|highfi|highfidelity/.test(normal)) return { id: "high_fidelity", label: "High-fidelity", evidence: "observed", count: 0 };
  if (/avl|bet|vlm|lowfi|lowfidelity/.test(normal)) return { id: "low_fidelity", label: "Low-fidelity", evidence: "observed", count: 0 };
  return { id: "observed", label: text === "observed" ? "Observed" : text, evidence: "observed", count: 0 };
}

function fallbackExplorer(data: V3WorkbenchData, projectId: string, branch: string): AdbExplorerPayload {
  const rows = data.rows as unknown as Record<string, unknown>[];
  const numericFields = Array.from(new Set(rows.slice(0, 500).flatMap((row) =>
    Object.entries(row)
      .filter(([field, value]) => numericValue(value) !== null && !isMetadata(field))
      .map(([field]) => field),
  )));
  const outputs = numericFields.filter(isOutput);
  const axes = numericFields.filter((field) => !outputs.includes(field) && isAxis(field));
  const flexibleOutputs = outputs.length ? outputs : numericFields.filter((field) => !axes.includes(field));
  const defaultOutput = OUTPUT_PRIORITY.find((field) => flexibleOutputs.includes(field)) ?? flexibleOutputs[0] ?? null;
  const layers = new Map<string, AdbExplorerLayer>();
  const projectedRows = rows.slice(0, 5000).map((row, index) => {
    const layer = inferLayer(row);
    layers.set(layer.id, { ...layer, count: (layers.get(layer.id)?.count ?? 0) + 1 });
    return {
      ...row,
      _row_id: String(row.runId ?? row.run_id ?? row.name ?? rowKey(row as ThreadRow) ?? `row-${index}`),
      _model_layer: layer.id,
      _evidence: layer.evidence,
    };
  });
  const correlations = Object.fromEntries(flexibleOutputs.map((output) => [
    output,
    numericFields
      .filter((field) => field !== output)
      .map((field) => {
        const r = pearson(rows, field, output);
        return r == null ? null : { field, label: labelFor(field), r, evidence: isAxis(field) ? "observed" : "model-conditioned" } satisfies AdbCorrelationScore;
      })
      .filter((item): item is AdbCorrelationScore => item !== null)
      .sort((left, right) => Math.abs(right.r) - Math.abs(left.r))
      .slice(0, 8),
  ]));
  return {
    project_id: projectId,
    branch,
    row_count: rows.length,
    sample_count: projectedRows.length,
    outputs: flexibleOutputs.map((field) => ({ id: field, label: labelFor(field), role: "output", evidence: /model|pred|surrogate|corrected|teacher/i.test(field) ? "model-conditioned" : "observed", priority: OUTPUT_PRIORITY.indexOf(field), ...columnStats(rows, field) })),
    axes: axes.map((field) => ({ id: field, label: labelFor(field), role: "axis", held_default: columnStats(rows, field).mean, ...columnStats(rows, field) })),
    default_output: defaultOutput,
    default_axes: { x: axes[0] ?? null, y: axes[1] ?? null },
    model_layers: Array.from(layers.values()),
    rows: projectedRows,
    correlations,
    findings: [],
    evidence: {
      raw_count: projectedRows.length,
      direct_count: projectedRows.filter((row) => row._evidence === "observed").length,
      compatible_count: projectedRows.filter((row) => row._evidence === "observed").length,
      corrected_count: projectedRows.filter((row) => row._model_layer === "corrected").length,
    },
    warnings: flexibleOutputs.length ? [] : ["No numeric outputs were discovered in the current table."],
  };
}

function bestDefaultOutput(explorer: AdbExplorerPayload | undefined): string {
  if (!explorer?.outputs.length) return "";
  return explorer.default_output ?? OUTPUT_PRIORITY.find((field) => explorer.outputs.some((output) => output.id === field)) ?? explorer.outputs[0]!.id;
}

function selectedOutputLabel(explorer: AdbExplorerPayload, selectedOutput: string): string {
  return explorer.outputs.find((output) => output.id === selectedOutput)?.label ?? labelFor(selectedOutput);
}

function compactNumber(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "-";
  const abs = Math.abs(value);
  // Tiny-but-nonzero magnitudes (e.g. a 8.7e-16 RMSE "max move") render as an
  // unreadable run of zeros via toLocaleString — switch to scientific notation.
  if (abs !== 0 && (abs < 1e-4 || abs >= 1e7)) return value.toExponential(2);
  if (abs >= 1000) return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (abs >= 10) return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return value.toLocaleString(undefined, { maximumSignificantDigits: 4 });
}

function axisDefaultValue(axis: AdbExplorerColumn): number {
  return numericValue(axis.held_default) ?? numericValue(axis.mean) ?? numericValue(axis.min) ?? 0;
}

function initialHeldValues(axes: AdbExplorerColumn[]): Record<string, number> {
  return Object.fromEntries(axes.map((axis) => [axis.id, axisDefaultValue(axis)]));
}

function axisOptionLabel(axis: AdbExplorerColumn | undefined): string {
  return axis ? axis.label : "axis";
}

function trustLimitsForLevel(level: "strict" | "balanced" | "wide"): TailorTrustLimits {
  if (level === "strict") {
    return {
      ...DEFAULT_TAILOR_TRUST_LIMITS,
      hiddenRmsLimit: 0.12,
      hiddenMaxDeltaLimit: 0.24,
      correctionAbsLimit: 0.12,
      correctionRelativeLimit: 0.25,
      compatibleRmsLimit: 0.055,
      compatibleMaxDeltaLimit: 0.14,
    };
  }
  if (level === "wide") {
    return {
      ...DEFAULT_TAILOR_TRUST_LIMITS,
      hiddenRmsLimit: 0.26,
      hiddenMaxDeltaLimit: 0.52,
      correctionAbsLimit: 0.28,
      correctionRelativeLimit: 0.55,
      compatibleRmsLimit: 0.105,
      compatibleMaxDeltaLimit: 0.24,
    };
  }
  return DEFAULT_TAILOR_TRUST_LIMITS;
}

function evidenceModeDescription(mode: TailorEvidenceMode): string {
  if (mode === "raw") return "raw projected samples";
  if (mode === "compatible") return "direct compatible slice evidence";
  return "model-conditioned corrected slice evidence";
}

function reviewSentence(explorer: AdbExplorerPayload, selectedOutput: string): string {
  const existing = explorer.findings.find((finding) => finding.includes(selectedOutputLabel(explorer, selectedOutput))) ?? explorer.findings[0];
  if (existing) return existing;
  const drivers = (explorer.correlations[selectedOutput] ?? []).filter((item) => explorer.axes.some((axis) => axis.id === item.field));
  if (!drivers.length) return `${selectedOutputLabel(explorer, selectedOutput)} has insufficient direct evidence for a driver review.`;
  const strong = drivers.filter((item) => Math.abs(item.r) >= 0.45).slice(0, 2).map((item) => item.label);
  const weak = drivers.find((item) => Math.abs(item.r) < 0.45);
  const primary = strong.length ? `primarily ${strong.join("/")}` : "no dominant direct driver";
  return `${selectedOutputLabel(explorer, selectedOutput)} is ${primary}${weak ? `, weak ${weak.label} coupling` : ""}, ${explorer.evidence.direct_count < 8 ? "insufficient direct evidence at this held slice" : `${explorer.evidence.direct_count} direct samples in view`}.`;
}

function layerColor(index: number): string {
  return MODEL_LAYER_COLORS[index % MODEL_LAYER_COLORS.length]!;
}

function outputLookupKeys(output: string): string[] {
  return Array.from(new Set([output, ...(OUTPUT_VALUE_ALIASES[output] ?? []), labelFor(output)]));
}

function pickByToken(source: Record<string, unknown> | undefined, wanted: Set<string>): number | null {
  // Case-insensitive, punctuation-insensitive key match so the composite's
  // lowercase output ids (cl, cd, cmx, l_d) resolve against the Mannequin's
  // mixed-case output keys (CL, CD, Cmx) without enumerating every alias.
  if (!source) return null;
  for (const [key, raw] of Object.entries(source)) {
    if (wanted.has(token(key))) {
      const value = numericValue(raw);
      if (value !== null) return value;
    }
  }
  return null;
}

function livePointOutputValue(point: SamplePoint, output: string): number | null {
  const keys = outputLookupKeys(output);
  const wanted = new Set(keys.map(token));
  for (const source of [point.outputs, point.truthOutputs]) {
    if (!source) continue;
    for (const key of keys) {
      const value = numericValue(source[key]);
      if (value !== null) return value;
    }
    const tokenMatch = pickByToken(source, wanted);
    if (tokenMatch !== null) return tokenMatch;
  }
  if (point.predicted) {
    for (const key of keys) {
      const value = numericValue(point.predicted[key]?.mean);
      if (value !== null) return value;
    }
    for (const [key, pred] of Object.entries(point.predicted)) {
      if (wanted.has(token(key))) {
        const value = numericValue(pred?.mean);
        if (value !== null) return value;
      }
    }
  }
  return null;
}

function liveRowsForPoints(points: SamplePoint[], selectedOutput: string, model: ReturnType<typeof fitTailorSliceModel>): Record<string, unknown>[] {
  return points.map((point) => {
    const row: Record<string, unknown> = {
      ...point.inputs,
      ...(point.truthOutputs ?? {}),
      ...(point.outputs ?? {}),
      _row_id: point.id,
      _node_id: `adaptive-sampling:${point.id}`,
      _model_layer: TAILOR_LIVE_LAYER_ID,
      _evidence: point.status === "done" ? "observed" : "model-conditioned",
      _tailor_status: point.status,
      _tailor_iter: point.iter,
      _tailor_selected_by: point.samplerDecision?.selectedBy ?? "initial_design",
    };
    for (const [key, prediction] of Object.entries(point.predicted ?? {})) {
      if (numericValue(row[key]) === null) row[key] = prediction.mean;
      row[`${key}_sigma`] = prediction.sigma;
    }
    if (selectedOutput) {
      const selectedValue = livePointOutputValue(point, selectedOutput);
      if (selectedValue !== null) {
        row[selectedOutput] = selectedValue;
      } else if (model) {
        row[selectedOutput] = predictTailorSliceModel(model, row);
      }
    }
    return row;
  });
}

function ExplorerStat({ label, value, tone }: { label: string; value: ReactNode; tone?: "warn" | "ok" }) {
  return <span className={`v5-adb-stat ${tone ? `is-${tone}` : ""}`}><small>{label}</small><strong>{value}</strong></span>;
}

// ADB Generator entry. For composite-resident projects (those with seeded
// /api/composite sources) this delegates to the composite engine surface —
// the convergence of the standalone composite workbench into ADB/Tailor
// (TRANSPORT.md §7). Everything else gets the row-driven client-fit explorer.
// A single probe hook runs every render, so the classic/composite choice
// stays rules-of-hooks-safe (each branch is its own component).
export function AdbExplorerSurface(props: SurfaceProps) {
  const { projectId, branch, persistedProject } = props;
  const composite = useQuery({
    queryKey: ["composite-sources", projectId, branch],
    queryFn: () => fetchCompositeSources({ projectId, branch }),
    enabled: persistedProject,
    staleTime: 60_000,
    retry: false,
  });
  if (persistedProject && composite.isLoading) {
    return (
      <main className="v5-main v5-adb-page">
        <section className="v5-adb-shell">
          <div className="v5-adb-loading"><Icons.Refresh size={18} />Loading database…</div>
        </section>
      </main>
    );
  }
  if (persistedProject && composite.isError) {
    return (
      <main className="v5-main v5-adb-page">
        <section className="v5-adb-shell">
          <div className="v5-adb-loading" data-testid="composite-sources-error">
            {composite.error instanceof Error ? composite.error.message : "Unable to load composite sources."}
          </div>
        </section>
      </main>
    );
  }
  if (composite.data && composite.data.sources.length > 0) {
    return (
      <Suspense fallback={<div className="v5-adb-loading"><Icons.Refresh size={18} />Loading composite database…</div>}>
        <LazyAdbCompositeSurface {...props} sources={composite.data} />
      </Suspense>
    );
  }
  return <AdbExplorerClassic {...props} />;
}

function AdbExplorerClassic(props: SurfaceProps) {
  const { data, projectId, branch, persistedProject } = props;
  const query = useQuery({
    queryKey: ["v5-adb-explorer", projectId, branch],
    queryFn: () => fetchAdbExplorer(projectId, branch, 5000),
    enabled: persistedProject,
    staleTime: 60_000,
  });
  const fallback = useMemo(() => fallbackExplorer(data, projectId, branch), [branch, data, projectId]);
  const explorer = query.data ?? fallback;
  return (
    <AdbExplorerBody
      {...props}
      explorer={explorer}
      queryLoading={query.isLoading && !query.data}
      queryError={query.error ? "Production ADB route was unavailable; showing the current V5 table fallback." : null}
    />
  );
}

export type AdbExplorerExtraContext = {
  selectedOutput: string;
  focusXAxis: string;
  focusYAxis: string;
  heldValues: Record<string, number>;
  liveRows: Record<string, unknown>[];
  rows: Record<string, unknown>[];
  axes: AdbExplorerColumn[];
  evidenceMode: TailorEvidenceMode;
  trustLevel: "strict" | "balanced" | "wide";
  trustLimits: TailorTrustLimits;
  resolveValue: (row: Record<string, unknown>, xKey?: string, yKey?: string) => number | null;
};

/** Context passed to a composite-supplied high-D correlogram (FXC-9009). It gets
 *  the visible rows + selected output/axis labels, and a `renderDefault()` thunk
 *  that renders the classic binned matrix verbatim — so a GP-backed correlogram
 *  can fall back to it (e.g. when no baked field exists) without duplicating it. */
export type AdbExplorerCorrelogramContext = {
  rows: Record<string, unknown>[];
  selectedOutput: string;
  focusXAxis: string;
  focusYAxis: string;
  heldValues: Record<string, number>;
  axes: AdbExplorerColumn[];
  axisLabels: Record<string, string>;
  /** The explorer's Raw/Compatible/Corrected tab state — the GP correlogram
   * applies it to the projected run POINTS (never the surface, FXC-9009). */
  evidenceMode: TailorEvidenceMode;
  /** Resolve a row's displayed output under Raw/Compatible/Corrected using the
   * same fitted slice model and trust gate as the native correlogram. */
  resolveValue: (row: Record<string, unknown>, xKey?: string, yKey?: string) => number | null;
  renderDefault: () => ReactNode;
};

// Stable empty selection so the project-scope guard doesn't allocate per render.
const EMPTY_CAMPAIGN: AdaptiveCampaignSelection = {};

export interface AdbExplorerBodyProps extends SurfaceProps {
  explorer: AdbExplorerPayload;
  queryLoading?: boolean;
  queryError?: string | null;
  /** Composite mode: extra controls injected into the toolbar (e.g. trust-policy select). */
  headerExtra?: ReactNode | ((ctx: AdbExplorerExtraContext) => ReactNode);
  /** Composite mode: panels rendered after the correlogram matrix (buildup, trust map, run-next). */
  renderExtraPanels?: (ctx: AdbExplorerExtraContext) => ReactNode;
  /** Composite mode: panels rendered BEFORE the correlogram, right under the
   * slice strip — used for the Trust Surfaces hero so it leads, with the
   * classic 2D/3D/high-D correlogram exploration following below. */
  renderTopPanels?: (ctx: AdbExplorerExtraContext) => ReactNode;
  /** Composite mode: intercept a correlogram point click (opens the provenance
   * drawer by condition key); receives the currently-selected output/quantity. */
  onPointSelect?: (row: Record<string, unknown>, selectedOutput: string) => void;
  /** Show the correlogram matrix + its Raw/Compatible/Corrected evidence-mode
   * tabs + fit audit + evidence drawer. Default true (classic explorer);
   * composite suppresses it in 2D/3D — its Trust Surfaces panel is the surface
   * view and its trust policy / provenance cover the evidence story. The
   * X/Y/held slice controls stay (they re-slice the surfaces). */
  showCorrelogram?: boolean;
  /** Composite high-D: replace the binned SurfaceCorrelogram matrix with a
   * caller-supplied correlogram (the Tailor GP correlogram — every cell a smooth
   * GP slice). Receives the visible rows + a `renderDefault()` thunk that renders
   * the classic binned matrix, so the GP correlogram can fall back to it when no
   * baked field exists. Only consulted when `showCorrelogram` is true. */
  renderCorrelogram?: (ctx: AdbExplorerCorrelogramContext) => ReactNode;
  /** Hide the Y-axis selector (composite 2D mode): in a 2D line view the
   * vertical axis IS the selected output, so a condition Y-axis picker is
   * meaningless. When set, Y stops being a plot axis and folds back into the
   * held-axis controls so its slice value stays adjustable. */
  hideYAxis?: boolean;
  /** Hide the slice strip's X/Y selects + held number inputs (composite 3D /
   * high-D): the GP views own their axes + held-condition strip, so a second,
   * UNLINKED outer control set is misleading (its X/Y didn't drive the GP
   * plane). One source of truth — the GP components; the outer evidence tabs
   * stay (they drive the GP point modes). */
  hideSliceAxes?: boolean;
  /** Keep the native evidence tabs visible when a composed 2-D/3-D panel
   * replaces the correlogram body. */
  showEvidenceTabs?: boolean;
  /** Optional controlled evidence mode for guided demos and other composed surfaces. */
  evidenceMode?: TailorEvidenceMode;
  onEvidenceModeChange?: (mode: TailorEvidenceMode) => void;
  /** Guided replay: constrain live campaign evidence to the points currently revealed. */
  livePointsOverride?: SamplePoint[];
  /** Keep the high-dimensional correlogram scientific and bounded by offering
   * only condition axes (plus the selected response used as Z). The general
   * explorer still defaults to ranking every numeric response/derived field. */
  correlogramConditionAxesOnly?: boolean;
}

// Presentational ADB explorer body — the toolbar, review strip, Tailor
// slice strip (Raw/Compatible/Corrected), adaptive sampler, correlogram
// (2D/3D/high-D surfaces), and evidence drawer. Driven entirely by the
// `explorer` payload it is given, so it renders identically whether that
// payload came from the run-table ADB route (classic) or from the composite
// engine (composite mode, via headerExtra/renderExtraPanels/onPointSelect).
export function AdbExplorerBody(props: AdbExplorerBodyProps) {
  const {
    explorer, dataLoading, projectId, projectName, selectedRunId, onSelectRun, onOpenView,
    queryLoading, queryError, headerExtra, renderExtraPanels, renderTopPanels, onPointSelect,
    showCorrelogram = true, renderCorrelogram, hideYAxis = false, hideSliceAxes = false,
    showEvidenceTabs = showCorrelogram, evidenceMode: controlledEvidenceMode, onEvidenceModeChange,
    livePointsOverride, correlogramConditionAxesOnly = false,
  } = props;
  const [selectedOutput, setSelectedOutput] = useState(() => bestDefaultOutput(explorer));
  const [layerFilter, setLayerFilter] = useState("all");
  const [internalEvidenceMode, setInternalEvidenceMode] = useState<TailorEvidenceMode>("corrected");
  const evidenceMode = controlledEvidenceMode ?? internalEvidenceMode;
  const setEvidenceMode = (mode: TailorEvidenceMode) => {
    setInternalEvidenceMode(mode);
    onEvidenceModeChange?.(mode);
  };
  const [focusXAxis, setFocusXAxis] = useState(() => explorer.default_axes.x ?? explorer.axes[0]?.id ?? "");
  const [focusYAxis, setFocusYAxis] = useState(() => explorer.default_axes.y ?? explorer.axes.find((axis) => axis.id !== (explorer.default_axes.x ?? explorer.axes[0]?.id))?.id ?? "");
  const [heldValues, setHeldValues] = useState<Record<string, number>>(() => initialHeldValues(explorer.axes));
  // Slider/thumb feedback and the GP surface request stay urgent. The legacy
  // evidence audit and ND recommendation pass can visit hundreds of candidate
  // / row pairs, so let React finish those against a deferred snapshot instead
  // of blocking every pointer event.
  const deferredHeldValues = useDeferredValue(heldValues);
  const [trustLevel, setTrustLevel] = useState<"strict" | "balanced" | "wide">("balanced");
  // Live points come from a campaign launched on the Training surface (shared via
  // context), so the configurator isn't omnipresent here — we just overlay them.
  // Scope to this project so a campaign run elsewhere never leaks onto it.
  const { selection: campaignSelection } = useAdaptiveCampaign();
  const activeCampaign = campaignSelection.projectId === projectId ? campaignSelection : EMPTY_CAMPAIGN;
  const adoptedInputs = activeCampaign.adoptedInputs;
  const appliedAdoptedInputsRef = useRef<Record<string, number> | undefined>(undefined);
  const campaignLivePoints = useCampaignPoints(activeCampaign.tailorId);
  const livePoints = livePointsOverride ?? campaignLivePoints;

  useEffect(() => {
    if (!explorer.outputs.some((output) => output.id === selectedOutput)) {
      setSelectedOutput(bestDefaultOutput(explorer));
    }
  }, [explorer, selectedOutput]);

  useEffect(() => {
    setHeldValues((current) => {
      const next: Record<string, number> = {};
      let changed = Object.keys(current).length !== explorer.axes.length;
      for (const axis of explorer.axes) {
        const currentValue = current[axis.id];
        next[axis.id] = Number.isFinite(currentValue) ? currentValue! : axisDefaultValue(axis);
        if (next[axis.id] !== current[axis.id]) changed = true;
      }
      // Streamed campaign rows rebuild the explorer axis metadata. Preserve the
      // held-value object when its values did not change so corrected-mode
      // resolvers and High-D surfaces do not restart hydration unnecessarily.
      return changed ? next : current;
    });
  }, [explorer.axes]);

  useEffect(() => {
    if (!adoptedInputs) return;
    // `explorer.axes` is rebuilt whenever a streamed point arrives. The old
    // effect depended on that array and consequently re-applied the original
    // adopted slice after every render/query refresh, overwriting a slider as
    // soon as the user released it. An adoption is an explicit one-off action:
    // apply a particular set of values once and then leave local slice edits
    // under the user's control.
    if (appliedAdoptedInputsRef.current === adoptedInputs) return;
    appliedAdoptedInputsRef.current = adoptedInputs;
    setHeldValues((current) => {
      let changed = false;
      const next = { ...current };
      for (const [name, value] of Object.entries(adoptedInputs)) {
        if (Number.isFinite(value) && current[name] !== value) {
          next[name] = value;
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }, [adoptedInputs]);

  useEffect(() => {
    const axisIds = explorer.axes.map((axis) => axis.id);
    if (!axisIds.length) return;
    const defaultX = explorer.default_axes.x && axisIds.includes(explorer.default_axes.x) ? explorer.default_axes.x : axisIds[0]!;
    const nextX = axisIds.includes(focusXAxis) ? focusXAxis : defaultX;
    const defaultY = explorer.default_axes.y
      && axisIds.includes(explorer.default_axes.y)
      && explorer.default_axes.y !== nextX
      ? explorer.default_axes.y
      : axisIds.find((axis) => axis !== nextX) ?? nextX;
    if (focusXAxis !== nextX) setFocusXAxis(nextX);
    if (!axisIds.includes(focusYAxis) || focusYAxis === nextX) setFocusYAxis(defaultY);
  }, [explorer.axes, explorer.default_axes.x, explorer.default_axes.y, focusXAxis, focusYAxis]);

  const baseVisibleRows = useMemo(() => {
    if (layerFilter === "all" || layerFilter === TAILOR_LIVE_LAYER_ID) return explorer.rows;
    return explorer.rows.filter((row) => row._model_layer === layerFilter);
  }, [explorer.rows, layerFilter]);
  const baseTailorModel = useMemo(
    () => selectedOutput ? fitTailorSliceModel(baseVisibleRows, explorer.axes, selectedOutput) : null,
    [baseVisibleRows, explorer.axes, selectedOutput],
  );
  const liveRows = useMemo(() => liveRowsForPoints(livePoints, selectedOutput, baseTailorModel), [baseTailorModel, livePoints, selectedOutput]);
  const modelLayers = useMemo(() => {
    const baseLayers = explorer.model_layers.filter((layer) => layer.id !== TAILOR_LIVE_LAYER_ID);
    if (!liveRows.length) return baseLayers;
    return [
      ...baseLayers,
      {
        id: TAILOR_LIVE_LAYER_ID,
        label: "Live Tailor",
        evidence: "model-conditioned" as const,
        count: liveRows.length,
      },
    ];
  }, [explorer.model_layers, liveRows.length]);

  useEffect(() => {
    if (layerFilter !== "all" && !modelLayers.some((layer) => layer.id === layerFilter)) {
      setLayerFilter("all");
    }
  }, [modelLayers, layerFilter]);

  const visibleRows = useMemo(() => {
    if (layerFilter === TAILOR_LIVE_LAYER_ID) return liveRows;
    if (layerFilter === "all") return [...baseVisibleRows, ...liveRows];
    return baseVisibleRows;
  }, [baseVisibleRows, layerFilter, liveRows]);

  const trustLimits = useMemo(() => trustLimitsForLevel(trustLevel), [trustLevel]);
  const tailorModel = useMemo(
    () => selectedOutput ? fitTailorSliceModel(visibleRows, explorer.axes, selectedOutput) : null,
    [explorer.axes, selectedOutput, visibleRows],
  );
  const tailorEvidence = useMemo(
    () => selectedOutput
      ? summarizeTailorEvidence(visibleRows, tailorModel, selectedOutput, deferredHeldValues, focusXAxis, focusYAxis, trustLimits)
      : { raw: 0, compatible: 0, correctedTotal: 0, correctedTrusted: 0, correctedFlagged: 0, correctedRejected: 0, meanHiddenRms: null, maxHiddenJump: null, maxCorrectionDelta: null },
    [deferredHeldValues, focusXAxis, focusYAxis, selectedOutput, tailorModel, trustLimits, visibleRows],
  );
  const tailorSuggestions = useMemo(
    () => selectedOutput
      ? recommendTailorRuns({ rows: visibleRows, model: tailorModel, axes: explorer.axes, heldValues: deferredHeldValues, output: selectedOutput, xKey: focusXAxis, yKey: focusYAxis, trust: trustLimits })
      : [],
    [deferredHeldValues, explorer.axes, focusXAxis, focusYAxis, selectedOutput, tailorModel, trustLimits, visibleRows],
  );
  const zValueResolver = useMemo(() => {
    if (evidenceMode === "raw" || !selectedOutput) return undefined;
    return (context: { row: Record<string, unknown>; xKey: string; yKey: string; zKey: string }) =>
      tailorValueForMode(evidenceMode, context.row, tailorModel, selectedOutput, deferredHeldValues, context.xKey, context.yKey, trustLimits);
  }, [deferredHeldValues, evidenceMode, selectedOutput, tailorModel, trustLimits]);
  const resolveEvidenceValue = (row: Record<string, unknown>, xKey = focusXAxis, yKey = focusYAxis): number | null => {
    if (!selectedOutput) return null;
    if (evidenceMode === "raw") return numericValue(row[selectedOutput]);
    return tailorValueForMode(evidenceMode, row, tailorModel, selectedOutput, deferredHeldValues, xKey, yKey, trustLimits);
  };
  // Model-derived departure frontier (NOT a measurement) for the focus pane.
  const tailorRidge = useMemo(
    () => selectedOutput
      ? deriveFeatureRidge({ model: tailorModel, heldValues: deferredHeldValues, output: selectedOutput, xKey: focusXAxis, yKey: focusYAxis, suggestions: tailorSuggestions })
      : null,
    [deferredHeldValues, focusXAxis, focusYAxis, selectedOutput, tailorModel, tailorSuggestions],
  );
  // Overlay payloads: project Tailor's suggested runs and the frontier onto the
  // correlogram cell that matches the focus axes. Click-to-adopt mirrors the
  // text-list button (writes the suggestion's full ND values into heldValues).
  const suggestionOverlay = useMemo(() => {
    if (!tailorSuggestions.length) return undefined;
    const maxScore = Math.max(1e-9, ...tailorSuggestions.map((item) => item.score));
    return {
      xAxis: focusXAxis,
      yAxis: focusYAxis,
      points: tailorSuggestions
        .filter((item) => Number.isFinite(item.values[focusXAxis]) && Number.isFinite(item.values[focusYAxis]))
        .map((item) => ({
          id: item.id,
          x: item.values[focusXAxis]!,
          y: item.values[focusYAxis]!,
          z: item.predicted,
          emphasis: Math.max(0, Math.min(1, item.score / maxScore)),
          tooltip: [
            `predicted ${selectedOutput} ${compactNumber(item.predicted)}`,
            item.moves.length ? `off-slice: ${item.moves.map((move) => `${move.label} ${compactNumber(move.value)}`).join(" · ")}` : "on the held slice",
            `${item.evidence.trusted} trusted · ${item.evidence.flagged} flagged · ${item.evidence.rejected} rejected nearby`,
            item.reason,
          ].join("\n"),
        })),
    };
  }, [focusXAxis, focusYAxis, selectedOutput, tailorSuggestions]);
  const ridgeOverlay = useMemo(() => {
    if (!tailorRidge) return undefined;
    return {
      xAxis: tailorRidge.xAxis,
      yAxis: tailorRidge.yAxis,
      points: tailorRidge.points.map((point) => ({ x: point.x, y: point.y, z: point.predicted })),
      confidence: tailorRidge.confidence,
      caption: tailorRidge.caption,
    };
  }, [tailorRidge]);
  const adoptSuggestion = useMemo(() => {
    const adopter = buildSuggestionAdopter(tailorSuggestions);
    return (id: string) => setHeldValues((current) => adopter(id, current));
  }, [tailorSuggestions]);
  const visibleHeldAxes = useMemo(() => {
    const correlated = (explorer.correlations[selectedOutput] ?? []).map((item) => item.field);
    // In 2D (hideYAxis) the Y axis isn't a plot dimension — it's just another
    // held constant, so keep it in the held controls (only X is excluded).
    // A High-D correlogram has no single global plotted pair: each axis is held
    // in every cell that does not name it. Expose the complete shared slice so
    // users can move (for example) alpha while inspecting beta x elevator.
    return explorer.axes
      .filter((axis) => showCorrelogram
        || (axis.id !== focusXAxis && (hideYAxis || axis.id !== focusYAxis)))
      .sort((left, right) => {
        const leftIndex = correlated.indexOf(left.id);
        const rightIndex = correlated.indexOf(right.id);
        return (leftIndex < 0 ? 999 : leftIndex) - (rightIndex < 0 ? 999 : rightIndex);
      });
  }, [explorer.axes, explorer.correlations, focusXAxis, focusYAxis, selectedOutput, hideYAxis, showCorrelogram]);

  const numericColumns = useMemo<CorrelogramColumn[]>(() => {
    if (!selectedOutput) return [];
    const allColumns = [...explorer.axes, ...explorer.outputs];
    const byId = new Map(allColumns.map((column) => [column.id, column]));
    const correlationFields = (explorer.correlations[selectedOutput] ?? []).map((item) => item.field);
    const ranked = rankColumnsByCorrelationTo(
      visibleRows,
      allColumns.map((column) => ({ field: column.id, headerName: column.label })),
      selectedOutput,
    ).map((score) => score.field);
    const fields = Array.from(new Set(correlogramConditionAxesOnly
      ? [selectedOutput, ...explorer.axes.map((axis) => axis.id)]
      : [
          selectedOutput,
          ...correlationFields,
          ...explorer.axes.map((axis) => axis.id),
          ...ranked,
        ])).filter((field) => visibleRows.some((row) => numericValue(row[field]) !== null));
    return fields.slice(0, 24).map((field) => {
      const column = byId.get(field);
      const output = explorer.outputs.find((item) => item.id === field);
      const corr = explorer.correlations[selectedOutput]?.find((item) => item.field === field);
      const role: CorrelogramColumn["role"] = output ? "response" : explorer.axes.some((axis) => axis.id === field) ? "condition" : "derived";
      return {
        key: field,
        header: column?.label ?? labelFor(field),
        role,
        evidence: output?.evidence ?? corr?.evidence ?? "observed",
        source: output?.evidence === "model-conditioned" || corr?.evidence === "model-conditioned" ? "model" : "observed",
      };
    });
  }, [correlogramConditionAxesOnly, explorer, selectedOutput, visibleRows]);

  const groups = useMemo(() => {
    if (modelLayers.length <= 1) return undefined;
    return modelLayers.map((layer, index) => ({
      key: layer.id,
      label: layer.label,
      color: layer.id === TAILOR_LIVE_LAYER_ID ? TAILOR_LIVE_LAYER_COLOR : layerColor(index),
      rows: visibleRows.filter((row) => row._model_layer === layer.id),
    })).filter((group) => group.rows.length > 0);
  }, [modelLayers, visibleRows]);

  const selectedRowIdx = selectedRunId ? visibleRows.findIndex((row) => String(row._row_id ?? row.runId ?? row.name) === selectedRunId || rowKey(row as ThreadRow) === selectedRunId) : -1;
  const selectedRow = selectedRowIdx >= 0 ? visibleRows[selectedRowIdx] : null;
  const selectedTailorAudit = selectedRow && selectedOutput
    ? correctionForPair(selectedRow, tailorModel, selectedOutput, deferredHeldValues, focusXAxis, focusYAxis, trustLimits)
    : null;
  const output = explorer.outputs.find((item) => item.id === selectedOutput);
  const storedWarning = explorer.warnings[0]?.includes("corrected/model-transported") && tailorModel
    ? "No stored corrected rows were found; Tailor is synthesizing corrected-to-slice evidence from the fitted response model."
    : explorer.warnings[0];
  const evidenceWarning = evidenceMode === "compatible" && tailorEvidence.compatible < 3
    ? `Too few compatible points at this held slice (${tailorEvidence.compatible}). Read correlations as model-conditioned until Tailor samples this region.`
    : evidenceMode === "corrected" && tailorEvidence.correctedTrusted < 6
      ? `Few trusted corrected points at this held slice (${tailorEvidence.correctedTrusted}). Loosen trust or request direct samples.`
      : null;
  const warning = evidenceWarning ?? storedWarning ?? queryError ?? null;
  const topCorrelations = explorer.correlations[selectedOutput] ?? [];
  const totalRows = explorer.row_count + liveRows.length;
  const compositeContext = {
    selectedOutput,
    focusXAxis,
    focusYAxis,
    heldValues,
    liveRows,
    rows: visibleRows,
    axes: explorer.axes,
    evidenceMode,
    trustLevel,
    trustLimits,
    resolveValue: resolveEvidenceValue,
  };
  const headerExtraNode = typeof headerExtra === "function" ? headerExtra(compositeContext) : headerExtra;

  // The classic binned SurfaceCorrelogram matrix, as a thunk so a composite-
  // supplied GP correlogram can fall back to it verbatim (FXC-9009).
  const renderDefaultCorrelogram = (): ReactNode =>
    numericColumns.length >= 2 && visibleRows.length ? (
      <Suspense fallback={<div className="v5-adb-loading"><Icons.Refresh size={18} />Loading correlogram...</div>}>
        <DataExplorerCorrelogram
          rows={visibleRows}
          numericColumns={numericColumns}
          groups={groups && groups.length > 1 ? groups : undefined}
          colorByKey={groups && groups.length > 1 ? undefined : "_model_layer"}
          zAxisKey={selectedOutput || undefined}
          zAxisLabel={selectedOutput ? `${selectedOutputLabel(explorer, selectedOutput)} · ${evidenceMode}` : undefined}
          zValueResolver={zValueResolver}
          selectedRowIdx={selectedRowIdx >= 0 ? selectedRowIdx : undefined}
          maxColumns={14}
          // The tutorial's bounded High-D view is already scoped to the seven
          // sampled conditions. Keep every one visible by default so "High-D"
          // really means the complete campaign space; users can still prune
          // manually from the matrix toolbar. The general ADB explorer retains
          // its compact correlation-ranked defaults.
          defaultPruneBoringAxes={!correlogramConditionAxesOnly}
          defaultHideWeakCorrelations={!correlogramConditionAxesOnly}
          weakCorrelationThreshold={0.16}
          suggestionOverlay={suggestionOverlay}
          ridgeOverlay={ridgeOverlay}
          onSuggestionAdopt={adoptSuggestion}
          onPointClick={(rowIdx) => {
            const row = visibleRows[rowIdx];
            if (!row) return;
            if (onPointSelect) { onPointSelect(row, selectedOutput); return; }
            onSelectRun(String(row._row_id ?? row.runId ?? row.name ?? rowKey(row as ThreadRow)), String(row._node_id ?? row.rowNodeId ?? "") || null);
          }}
        />
      </Suspense>
    ) : (
      <div className="v5-adb-empty">
        <Icons.GridIcon size={22} />
        <strong>Need more numeric data</strong>
        <span>ADB exploration needs at least two numeric fields and one output in the selected layer.</span>
      </div>
    );
  // Axis-id → label map for the GP correlogram's strip + cell captions.
  const correlogramAxisLabels = Object.fromEntries(
    [...explorer.axes, ...explorer.outputs].map((column) => [column.id, column.label]),
  );

  return (
    <main className="v5-main v5-adb-page">
      <section className={renderTopPanels ? "v5-adb-shell v5-adb-shell--top" : "v5-adb-shell"}>
        <header className="v5-adb-toolbar">
          <div className="v5-adb-title">
            <span className="v5-eyebrow">ADB Generator</span>
            <strong>{projectName}</strong>
          </div>
          <label className="v5-adb-select">
            <span>Output</span>
            <select value={selectedOutput} onChange={(event) => setSelectedOutput(event.target.value)}>
              {explorer.outputs.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
            </select>
          </label>
          <div className="v5-adb-layer-tabs" aria-label="Model layer filter">
            <button type="button" className={layerFilter === "all" ? "is-active" : ""} onClick={() => setLayerFilter("all")}>All</button>
            {modelLayers.map((layer) => (
              <button key={layer.id} type="button" className={layerFilter === layer.id ? "is-active" : ""} onClick={() => setLayerFilter(layer.id)}>
                {layer.label}
              </button>
            ))}
          </div>
          {headerExtraNode}
          <button className="v5-text-button" type="button" onClick={() => onOpenView("data")}><Icons.Table size={13} />Table</button>
        </header>

        <div className="v5-adb-review">
          <div>
            <strong>{selectedOutput ? selectedOutputLabel(explorer, selectedOutput) : "No output"}</strong>
            <span>{selectedOutput ? reviewSentence(explorer, selectedOutput) : "No numeric outputs were discovered in this project."}</span>
          </div>
          <div className="v5-adb-stats">
            <ExplorerStat label="Rows" value={`${visibleRows.length.toLocaleString()} / ${totalRows.toLocaleString()}`} />
            <ExplorerStat label="Outputs" value={explorer.outputs.length} />
            <ExplorerStat label="Raw" value={tailorEvidence.raw.toLocaleString()} tone={tailorEvidence.raw < 8 ? "warn" : "ok"} />
            <ExplorerStat label="Compatible" value={tailorEvidence.compatible.toLocaleString()} tone={tailorEvidence.compatible < 3 ? "warn" : "ok"} />
            <ExplorerStat label="Corrected" value={`${tailorEvidence.correctedTrusted.toLocaleString()}/${tailorEvidence.correctedTotal.toLocaleString()}`} tone={tailorEvidence.correctedTrusted ? "ok" : "warn"} />
          </div>
        </div>

        <div className={`v5-adb-warning ${warning ? "" : "is-empty"}`} aria-hidden={warning ? undefined : true}>
          {warning ? <><Icons.Info size={14} />{warning}</> : null}
        </div>

        {activeCampaign.tailorId ? (
          <button type="button" className="v5-adb-campaign-pill" data-testid="adb-campaign-pill"
                  title="A Training-page sampler campaign is active; its proposed points are overlaid on the plots. Open Training to configure or run more."
                  onClick={() => onOpenView("training")}>
            <Icons.Sparkle size={13} />
            <span>Sampling{activeCampaign.aircraftLabel ? ` · ${activeCampaign.aircraftLabel}` : ""}</span>
            <strong>{livePoints.length} pts</strong>
            <em>Open Training</em>
          </button>
        ) : null}

        <section className="v5-adb-tailor-strip" aria-label="Slice controls">
          {showEvidenceTabs ? (
            <div className="v5-adb-evidence-tabs" aria-label="Point evidence mode">
              {TAILOR_EVIDENCE_MODES.map((mode) => (
                <button
                  key={mode.id}
                  type="button"
                  className={evidenceMode === mode.id ? "is-active" : ""}
                  onClick={() => setEvidenceMode(mode.id)}
                  title={mode.title}
                >
                  {mode.label}
                </button>
              ))}
            </div>
          ) : null}
          {/* hideSliceAxes (composite 3D/high-D): the GP views own their axes +
              held strip — a second UNLINKED X/Y/held set here was misleading. */}
          {hideSliceAxes ? null : (
            <label className="v5-adb-compact-select">
              <span>X</span>
              <select value={focusXAxis} onChange={(event) => setFocusXAxis(event.target.value)}>
                {explorer.axes.map((axis) => <option key={axis.id} value={axis.id}>{axis.label}</option>)}
              </select>
            </label>
          )}
          {hideSliceAxes || hideYAxis ? null : (
            <label className="v5-adb-compact-select">
              <span>Y</span>
              <select value={focusYAxis} onChange={(event) => setFocusYAxis(event.target.value)}>
                {explorer.axes.map((axis) => <option key={axis.id} value={axis.id}>{axis.label}</option>)}
              </select>
            </label>
          )}
          {showEvidenceTabs ? (
            <label className="v5-adb-compact-select" title="Corrected-point trust gate. Strict hides larger hidden-axis moves; wide keeps more model-conditioned evidence visible.">
              <span>Trust</span>
              <select value={trustLevel} onChange={(event) => setTrustLevel(event.target.value as "strict" | "balanced" | "wide")}>
                <option value="strict">Strict</option>
                <option value="balanced">Balanced</option>
                <option value="wide">Wide</option>
              </select>
            </label>
          ) : null}
          {showEvidenceTabs ? (
            <div className="v5-adb-tailor-audit">
              <span>{evidenceModeDescription(evidenceMode)}</span>
              <strong>{tailorModel ? `fit ${compactNumber(tailorModel.rmse)} RMSE` : "fit unavailable"}</strong>
              {evidenceMode === "corrected" ? <strong>max move {compactNumber(tailorEvidence.maxHiddenJump)}</strong> : null}
            </div>
          ) : null}
        </section>

        {!hideSliceAxes && visibleHeldAxes.length ? (
          <section className="v5-adb-slice-held" aria-label="Held-axis values for the current slice">
            <header>
              <div>
                <span className="v5-eyebrow">Slice definition</span>
                <strong>{showCorrelogram
                  ? "Each cell varies its labelled pair; every other axis is held at the value shown"
                  : "Every non-plotted axis is held at the value shown"}</strong>
              </div>
              <em>{showCorrelogram && evidenceMode === "raw"
                ? "Raw observations stay at their acquired coordinates; Corrected mode re-slices them"
                : `Move a slider to update this ${showCorrelogram ? "High-D matrix" : hideYAxis ? "2-D projection" : "3-D surface"}`}</em>
            </header>
            <div>
              {visibleHeldAxes.map((axis) => {
                const fallback = axisDefaultValue(axis);
                const min = numericValue(axis.min) ?? fallback - 1;
                const max = numericValue(axis.max) ?? fallback + 1;
                const value = Number.isFinite(heldValues[axis.id]) ? heldValues[axis.id]! : fallback;
                return (
                  <label key={axis.id} className="v5-held-input-slider">
                    <span>
                      <em>{axis.label}</em>
                      <input
                        className="v5-held-input-number"
                        aria-label={`${axis.label} held value`}
                        type="number"
                        min={min}
                        max={max}
                        step="any"
                        value={value}
                        onChange={(event) => {
                          const next = Number(event.target.value);
                          if (Number.isFinite(next)) {
                            setHeldValues((current) => ({ ...current, [axis.id]: Math.max(min, Math.min(max, next)) }));
                          }
                        }}
                      />
                    </span>
                    <input
                      aria-label={`${axis.label} held slider`}
                      type="range"
                      min={min}
                      max={max}
                      step="any"
                      value={Math.max(min, Math.min(max, value))}
                      onChange={(event) => setHeldValues((current) => ({ ...current, [axis.id]: Number(event.target.value) }))}
                    />
                  </label>
                );
              })}
            </div>
          </section>
        ) : null}

        {renderTopPanels?.(compositeContext)}

        {showCorrelogram ? (
        <>
        {(suggestionOverlay?.points.length || tailorRidge) ? (
          <div className="v5-adb-overlay-legend" title={tailorRidge?.caption}>
            {suggestionOverlay?.points.length ? (
              <span><i className="v5-adb-legend-suggestion" />Tailor suggested runs ({suggestionOverlay.points.length}) — click to adopt</span>
            ) : null}
            {tailorRidge ? (
              <span>
                <i className="v5-adb-legend-ridge" />
                Model-derived departure frontier ({(tailorRidge.confidence * 100).toFixed(0)}% conf.) — not measured
              </span>
            ) : null}
          </div>
        ) : null}
        <section className="v5-adb-matrix" aria-label="ADB correlogram">
          {queryLoading && dataLoading ? <div className="v5-adb-loading"><Icons.Refresh size={18} />Loading database outputs...</div> : null}
          {renderCorrelogram
            ? renderCorrelogram({
                rows: visibleRows,
                selectedOutput,
                focusXAxis,
                focusYAxis,
                heldValues,
                axes: explorer.axes,
                axisLabels: correlogramAxisLabels,
                evidenceMode,
                resolveValue: resolveEvidenceValue,
                renderDefault: renderDefaultCorrelogram,
              })
            : renderDefaultCorrelogram()}
        </section>
        </>
        ) : null}

        {showCorrelogram ? (
        <details className="v5-adb-drawer">
          <summary><span>Evidence, held axes, and output catalog</span><Icons.ChevronDown size={14} /></summary>
          <div className="v5-adb-drawer-grid">
            <section>
              <h3>Held axes</h3>
              <div className="v5-adb-held-grid">
                {explorer.axes.slice(0, 16).map((axis) => (
                  <label key={axis.id} title={`${axis.sample_count} samples, range ${compactNumber(axis.min)} to ${compactNumber(axis.max)}`}>
                    <span>{axis.label}</span>
                    <input
                      type="number"
                      step="any"
                      value={Number.isFinite(heldValues[axis.id]) ? heldValues[axis.id] : axisDefaultValue(axis)}
                      onChange={(event) => setHeldValues((current) => ({ ...current, [axis.id]: Number(event.target.value) }))}
                    />
                  </label>
                ))}
              </div>
            </section>
            <section>
              <h3>Top correlations</h3>
              <div className="v5-adb-correlation-list">
                {topCorrelations.slice(0, 8).map((item) => (
                  <span key={item.field} className={item.evidence === "model-conditioned" ? "is-model" : ""}>
                    <em>{item.label}</em>
                    <strong>{formatValue(item.r)}</strong>
                  </span>
                ))}
              </div>
            </section>
            <section>
              <h3>Corrected point audit</h3>
              {selectedTailorAudit ? (
                <div className="v5-adb-audit-list">
                  <span><em>Status</em><strong>{selectedTailorAudit.status}</strong></span>
                  <span><em>Hidden RMS</em><strong>{compactNumber(selectedTailorAudit.hiddenRms)}</strong></span>
                  <span><em>Max jump</em><strong>{compactNumber(selectedTailorAudit.hiddenMaxDelta)}</strong></span>
                  <span><em>Correction delta</em><strong>{compactNumber(selectedTailorAudit.correctionDelta)}</strong></span>
                  <span><em>Trust</em><strong>{compactNumber(selectedTailorAudit.trustScore)}</strong></span>
                </div>
              ) : (
                <p>Select a run to inspect how its value moves onto the current held slice.</p>
              )}
            </section>
            <section>
              <h3>Available outputs</h3>
              <div className="v5-adb-chip-list">
                {explorer.outputs.map((item) => (
                  <button key={item.id} type="button" className={item.id === selectedOutput ? "v5-adb-chip is-active" : "v5-adb-chip"} onClick={() => setSelectedOutput(item.id)}>
                    {item.label} <strong>{item.sample_count.toLocaleString()}</strong>
                  </button>
                ))}
              </div>
            </section>
            <section>
              <h3>Model layers</h3>
              <div className="v5-adb-chip-list">
                {modelLayers.map((layer) => (
                  <span key={layer.id} className={`v5-adb-chip ${layer.evidence === "model-conditioned" ? "is-model" : ""}`}>
                    {layer.label} <strong>{layer.count.toLocaleString()}</strong>
                  </span>
                ))}
              </div>
            </section>
            <section>
              <h3>Tailor next runs</h3>
              <div className="v5-adb-next-list">
                {tailorSuggestions.length ? tailorSuggestions.map((suggestion) => (
                  <button key={suggestion.id} type="button" onClick={() => setHeldValues((current) => ({ ...current, ...suggestion.values }))}>
                    <strong>{axisOptionLabel(explorer.axes.find((axis) => axis.id === focusXAxis))} {compactNumber(suggestion.values[focusXAxis])}</strong>
                    <strong>{axisOptionLabel(explorer.axes.find((axis) => axis.id === focusYAxis))} {compactNumber(suggestion.values[focusYAxis])}</strong>
                    {suggestion.moves.length ? (
                      <strong title="Hidden-axis moves: this proposal leaves the current 2D slice">
                        {suggestion.moves.map((move) => `${move.label} ${compactNumber(move.value)}`).join(" · ")}
                      </strong>
                    ) : null}
                    <span title="Nearby runs audited with the same trust gate as the raw/compatible/corrected views">
                      {suggestion.evidence.trusted} trusted · {suggestion.evidence.flagged} flagged · {suggestion.evidence.rejected} rejected nearby
                    </span>
                    <span>{suggestion.reason}{suggestion.sliceDistance > 0.02 ? " (off-slice)" : ""}</span>
                  </button>
                )) : (
                  <p>Need a fitted response model and two active axes before Tailor can suggest the next runs across the full axis space.</p>
                )}
              </div>
            </section>
            {output ? (
              <section>
                <h3>Selected output range</h3>
                <p>{output.label}: {compactNumber(output.min)} to {compactNumber(output.max)} across {output.sample_count.toLocaleString()} samples. Corrected mode is model-conditioned, not direct observation.</p>
              </section>
            ) : null}
          </div>
        </details>
        ) : null}

        {renderExtraPanels?.(compositeContext)}
      </section>
    </main>
  );
}
